import React from 'react'
import { Controller, type UseFormReturn } from 'react-hook-form'

import { Box, Stack, Typography } from '@mui/material'

import { TextField, SelectCountryField } from 'components'
import { AddressField, type ParsedAddress } from '@repo/ui'

import { textFieldHeightSx } from './ownerFormShared'

// VERA-914 (Stage B) — Address section: the owner's own address (owner_addresses
// table, VRG-99). Uses the shared Google Places `AddressField` (mirrors
// CreateAddressModal) so a selected suggestion fills the structured fields below
// it plus geocoding (latitude/longitude/place_id/formatted_address), which stay
// hidden RHF-only fields feeding the analytics scan map. All fields remain
// editable after autocomplete. Shared verbatim between Create Owner and Edit
// Owner. `defaultAddressLine` is read directly from the loaded owner record (not
// `methods.getValues()`) so AddressField's own uncontrolled input state
// initializes correctly at mount on Edit — mirrors the same timing fix applied
// to Birthday in OwnerDetailsFields.
export const OwnerAddressFields = ({
  methods,
  defaultAddressLine,
}: {
  methods: UseFormReturn<any>
  defaultAddressLine?: string | null
}) => {
  const { control, setValue } = methods

  // Autofill the structured fields + geocoding from a selected Places suggestion.
  const applyParsed = (p: ParsedAddress) => {
    setValue('address_line', p.address_line || '', { shouldDirty: true, shouldValidate: true })
    setValue('city', p.city || '', { shouldDirty: true, shouldValidate: true })
    setValue('state', p.state ?? '', { shouldDirty: true, shouldValidate: true })
    setValue('address_country', p.country || '', { shouldDirty: true, shouldValidate: true })
    setValue('postal_code', p.postal_code || '', { shouldDirty: true, shouldValidate: true })
    setValue('latitude', p.latitude, { shouldDirty: true })
    setValue('longitude', p.longitude, { shouldDirty: true })
    setValue('place_id', p.place_id, { shouldDirty: true })
    setValue('formatted_address', p.formatted_address, { shouldDirty: true })
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <Box>
        <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: '8px' }}>
          <Typography variant="body2">Address</Typography>
          <Typography variant="body4" color="text.secondary">(optional)</Typography>
        </Stack>
        <Controller
          name="address_line"
          control={control}
          render={({ field }) => (
            <AddressField
              apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
              defaultValue={defaultAddressLine ?? field.value ?? ''}
              onSelect={applyParsed}
              onInputChange={(value) => field.onChange(value)}
            />
          )}
        />
        <Typography variant="body4" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          Start typing an address. We&apos;ll fill in the rest. Every field stays editable.
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
        <Box sx={{ flex: '1 1 calc(50% - 12px)', minWidth: 200, ...textFieldHeightSx }}>
          <Controller
            name="city"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...methods}
                label="City"
                name="city"
                labelVariant="body2"
                helperText={fieldState.error?.message || 'optional'}
                value={field.value === undefined || field.value === null ? '' : String(field.value)}
                onValueChange={(value) => field.onChange(value)}
              />
            )}
          />
        </Box>
        <Box sx={{ flex: '1 1 calc(50% - 12px)', minWidth: 200, ...textFieldHeightSx }}>
          <Controller
            name="state"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...methods}
                label="State / Province"
                name="state"
                labelVariant="body2"
                helperText={fieldState.error?.message || 'optional'}
                value={field.value === undefined || field.value === null ? '' : String(field.value)}
                onValueChange={(value) => field.onChange(value)}
              />
            )}
          />
        </Box>
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '24px' }}>
        <Box sx={{ flex: '1 1 calc(50% - 12px)', minWidth: 200, ...textFieldHeightSx }}>
          <Controller
            name="postal_code"
            control={control}
            render={({ field, fieldState }) => (
              <TextField
                {...methods}
                label="Postal code"
                name="postal_code"
                labelVariant="body2"
                helperText={fieldState.error?.message || 'optional'}
                value={field.value === undefined || field.value === null ? '' : String(field.value)}
                onValueChange={(value) => field.onChange(value)}
              />
            )}
          />
        </Box>
        <Box sx={{ flex: '1 1 calc(50% - 12px)', minWidth: 200 }}>
          <Controller
            name="address_country"
            control={control}
            render={({ field, fieldState }) => (
              <Box>
                <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: '8px' }}>
                  <Typography variant="body2">Country</Typography>
                  <Typography variant="body4" color="text.secondary">(optional)</Typography>
                </Stack>
                <SelectCountryField
                  value={field.value || ''}
                  onChange={(code) => field.onChange(code)}
                  error={fieldState.error}
                />
              </Box>
            )}
          />
        </Box>
      </Box>
    </Box>
  )
}
